import {
	type CloudMemberFolderCommit,
	CloudMemberFolderError,
	type CloudMemberFolderUnit,
	type CloudMemberFolderUnitResult,
} from "@qorpera/cloud-client/cloud-member-folder";
import {
	cloudRoutineTriggerOfFile,
	routineFileSettings,
	routineFileTrigger,
} from "@qorpera/cloud-client/cloud-routine-file";
import {
	type CloudRoutine,
	CloudRoutineClient,
	type CloudRoutineOccurrence,
	type CloudRoutineTrigger,
} from "@qorpera/cloud-client/cloud-routines";
import {
	type CompanyStateEffort,
	companyStatePath,
	freeCompanyStateName,
	type RoutineFile,
	type RoutineTrigger,
	setCompanyStateSettings,
	writeRoutineFile,
} from "@qorpera/cloud-client/company-state-files";
import { type Cloud, CloudRpcError } from "./client.ts";
import {
	folderNames,
	invalid,
	parentPath,
	parseStateFile,
	readStateFile,
	rewriteStateFile,
	unsaved,
	type WebMemberFolder,
	withStateBody,
} from "./company-folder-writes.ts";
import { planLogin, type WebMemberConnection } from "./native-subscription.ts";
import { UUID_PATTERN } from "./parse.ts";
import type { HostedProfile } from "./types.ts";

/*
 * A routine is a file, `QorperaOS/agents/<handle>/routines/<key>.md` (folder core C4, P34r;
 * deploy/first-node/RUNTIME-COMPANY-FOLDER-C4-MEMBERS.md §6): every company is folder-v2, so a member's new routine,
 * edit, pause and resume write that file through the member commit route, as the member. The list and the runs still
 * come from the routine index. The file does not say who pays: before a save that needs a login (a new routine, a
 * resume, an edit of what a run pins), the member's account gets today's consent and binding, and the core takes the
 * routine's own login when it still pays for this member, else the member's newest one that pays for unattended work.
 * From 20260930095000 a routine keeps a login that serves its saver also when it cannot pay, and waits until a member
 * picks one (`setLogin`); `payer` says who pays and since when.
 */

/** A ChatGPT plan never pays for a file-change or webhook routine (the kernel refuses it too). */
const LISTENER_ON_PLAN = "A ChatGPT plan can't pay for routines that start on a file change or a webhook.";
/** The plain message when a routine's file is not saved. */
const NOT_SAVED = "The routine could not be saved.";
/** Tries of a new routine's file whose name another writer took since the listing. */
const CREATE_TRIES = 3;
/** Retries of an edit whose file changed between its read and the commit. */
const RETRIES = 3;

export interface WebRoutineDraft {
	/** The routine file's id: a new routine's comes from the view, one per draft, so a retry is the same routine. */
	clientRoutineId: string;
	staffAgentId: string;
	title: string;
	prompt: string;
	/** Every N minutes, or days at a time; no `replyPolicy` is `required`. An edit that keeps how its routine runs sends
	 * the opened file's own trigger (`cloudRoutineTriggerOfFile`), its reply the only change. */
	trigger: CloudRoutineTrigger;
	/** A new routine's. An edit keeps the file's own (an agent may have chosen medium or high). */
	effort: CompanyStateEffort;
	/** The routine's file as the editor opened it; none for a new routine. */
	opened?: WebRoutineFile;
}

/** A routine's file as one read found it: what the editor shows, and the version an edit of it names. */
export interface WebRoutineFile {
	readonly routineId: string;
	readonly path: string;
	readonly version: string;
	readonly text: string;
	readonly file: RoutineFile;
	/** The file's trigger as the editor shows it; none for one a member's editor does not make (a time window, a
	 * dynamic routine's next run, a file change). */
	readonly trigger?: CloudRoutineTrigger;
}

/** A routine with what the web reads beside the SDK's fields (migration 20260929970000): why a paused
 * routine waits, and the member its current version runs for. */
export type WebRoutine = CloudRoutine & {
	pauseReason?: string;
	runsForUserId?: string;
	/** The current version is a resume (20260929985000): `plan_turned_on` when its member turned on work while
	 * away for the ChatGPT plan that pays. */
	resumeReason?: string;
	resumedAt?: string;
	resumedByUserId?: string;
};

/** Who pays for a routine, as `qorpera_get_routine_payer` and `qorpera_set_routine_login` answer (migration
 * 20260930095000): a routine keeps its login, also when that login cannot pay, and only a member's pick moves it. */
export interface WebRoutinePayer {
	readonly routineId: string;
	readonly routineVersion: number;
	readonly status: CloudRoutine["status"];
	readonly pauseReason: string | null;
	/** The login its current version runs on; null when the cloud finds none behind it. */
	readonly connectionId: string | null;
	/** A member's own login or a company account; null without a login. */
	readonly payer: "member" | "company" | null;
	/** The member whose login pays; null for a company account. */
	readonly payerUserId: string | null;
	/** The member the routine runs for: its current version's saver. */
	readonly savedByUserId: string;
	/** Whether that login can pay for its work now. One that cannot leaves the routine waiting (needs_model_account)
	 * until someone picks a login, or keeps it while it is at its usage limit or needs a new sign-in. */
	readonly canPay: boolean;
	/** Why it can't: `login` (the routine waits for a pick), `usage_limit` (the login reached its usage limit; the
	 * routine's runs stop until it resets) or `sign_in` (its member must sign in to it again). Null when it can pay, and
	 * from a cloud that does not say. */
	readonly waitsFor: "login" | "usage_limit" | "sign_in" | null;
	/** When the payer last changed, else when the routine was made. */
	readonly since: string;
	/** Who changed it, and whether by a save or a pick; both null until a change is recorded. */
	readonly changedByUserId: string | null;
	readonly changeReason: "save" | "pick" | null;
}

/** Why the cloud refused a login for a routine: `unavailable` (the member may not change the routine, or it is
 * retired), `not_yours` (another member's login, or a company account picked by a contributor), `cannot_pay` (the
 * login cannot pay for work while its member is away now), `usage_limit` (it reached its usage limit), `sign_in` (its
 * member must sign in to it again), `plan_listener` (a ChatGPT plan for a file-change or webhook routine), `stopped`
 * (the company is stopped or being deleted). */
export type WebRoutineLoginRefusal =
	| "stopped"
	| "unavailable"
	| "not_yours"
	| "cannot_pay"
	| "usage_limit"
	| "sign_in"
	| "plan_listener";

export interface WebRoutines {
	list(): Promise<WebRoutine[]>;
	runs(): Promise<CloudRoutineOccurrence[]>;
	/** The routines whose file this member finds, by routine id. One without is read-only (no switch, no Edit): its
	 * file is out of the member's reach or from before the files, or the cloud does not list routines' files yet. */
	files(): Promise<ReadonlySet<string>>;
	/** Reads a routine's file for the editor. */
	open(routine: CloudRoutine): Promise<WebRoutineFile>;
	/** Writes a new routine's file, or an edit of one. `saveId` is the editor's: a request whose answer never came is
	 * sent again as it was by the next save with the same id, and only an answer frees it. "unchanged" when an edit
	 * changes nothing: nothing is written, since an unchanged save would resume a routine the platform paused. A save
	 * whose resent request was saved is "saved". A `WebRoutineChangedError` ends the save id; for a new routine it means
	 * a resent request made the routine and someone changed it before the draft's change since was written over it. */
	save(
		draft: WebRoutineDraft,
		account: WebMemberConnection | undefined,
		saveId: string,
	): Promise<"saved" | "unchanged">;
	/** Pauses or resumes, changing nothing else. A resume is paid by `account`; a pause needs no login. */
	setEnabled(
		routine: CloudRoutine,
		account: WebMemberConnection | undefined,
		enabled: boolean,
		saveId: string,
	): Promise<void>;
	/** Who pays for a routine now. Null on a cloud without the payer calls, which is then not asked again. */
	payer(routineId: string): Promise<WebRoutinePayer | null>;
	/** Runs a routine on one of the member's own logins, which makes them its saver: the login's consent and binding
	 * first, as a save's, then the pick. A routine that waited for a login runs again. The same null as `payer`; a
	 * refusal is a `WebRoutineLoginRefusedError`. */
	setLogin(routineId: string, account: WebMemberConnection): Promise<WebRoutinePayer | null>;
}

/** The routine has no file this member can change, or its file is gone: nothing was written. */
export class WebRoutineGoneError extends Error {}
/** What the editor shows of the routine changed since it was opened: the draft was not written; open it again (a new
 * routine's form, the routine a lost answer's replay made). */
export class WebRoutineChangedError extends Error {}
/** The routine has ended (concluded, or expired): nothing was written; the list says so. */
export class WebRoutineEndedError extends Error {}
/** The save needs a login and was given no account that can pay: nothing was written. */
export class WebRoutineNoPayerError extends Error {}
/** The cloud refused the login picked for a routine: nothing changed. */
export class WebRoutineLoginRefusedError extends Error {
	readonly reason: WebRoutineLoginRefusal;
	constructor(reason: WebRoutineLoginRefusal) {
		super("The cloud refused this login for the routine.");
		this.reason = reason;
	}
}

/** Clouds without the payer calls (before 20260930095000): asked no more while the page lives. */
const payerless = new WeakSet<Cloud>();
const ROUTINE_STATUSES: ReadonlySet<string> = new Set(["active", "paused", "expired", "concluded", "retired"]);
const ROUTINE_PAYER_WAITS: ReadonlySet<string> = new Set(["login", "usage_limit", "sign_in"]);

/** A `qorpera.routine-payer/v1` answer for `routineId`. Anything else is not read: a payer that names no member
 * for a member's login, or one for a company account, is as wrong as a missing field. */
export function parseRoutinePayer(value: unknown, routineId: string): WebRoutinePayer {
	const v = record(value);
	const id = (item: unknown): item is string => typeof item === "string" && UUID_PATTERN.test(item);
	const optionalId = (item: unknown) => item === null || id(item);
	const since = v.since;
	if (
		v.schema !== "qorpera.routine-payer/v1" ||
		v.routineId !== routineId ||
		!Number.isSafeInteger(v.routineVersion) ||
		(v.routineVersion as number) < 1 ||
		typeof v.status !== "string" ||
		!ROUTINE_STATUSES.has(v.status) ||
		!(v.pauseReason === null || (typeof v.pauseReason === "string" && /^[a-z_]{1,64}$/.test(v.pauseReason))) ||
		!optionalId(v.connectionId) ||
		!(v.connectionId === null
			? v.payer === null && v.payerUserId === null
			: v.payer === "member"
				? id(v.payerUserId)
				: v.payer === "company" && v.payerUserId === null) ||
		!id(v.savedByUserId) ||
		typeof v.canPay !== "boolean" ||
		// A login that can pay waits for nothing; one that can't says why (a cloud before the reason says nothing).
		!(v.waitsFor === undefined || (v.canPay ? v.waitsFor === null : waitsFor(v.waitsFor) !== null)) ||
		typeof since !== "string" ||
		since.length > 64 ||
		!/^\d{4}-\d{2}-\d{2}T/.test(since) ||
		!Number.isFinite(Date.parse(since)) ||
		!optionalId(v.changedByUserId) ||
		// No change recorded names no one who made it.
		!(v.changeReason === null ? v.changedByUserId === null : v.changeReason === "save" || v.changeReason === "pick")
	)
		throw new Error("Invalid cloud response");
	return {
		routineId,
		routineVersion: v.routineVersion as number,
		status: v.status as CloudRoutine["status"],
		pauseReason: v.pauseReason as string | null,
		connectionId: v.connectionId as string | null,
		payer: v.payer as WebRoutinePayer["payer"],
		payerUserId: v.payerUserId as string | null,
		savedByUserId: v.savedByUserId as string,
		canPay: v.canPay as boolean,
		waitsFor: v.canPay ? null : waitsFor(v.waitsFor),
		since,
		changedByUserId: v.changedByUserId as string | null,
		changeReason: v.changeReason as WebRoutinePayer["changeReason"],
	};
}

/** A payer read's reason, or null for anything else. */
function waitsFor(value: unknown): WebRoutinePayer["waitsFor"] {
	if (typeof value !== "string" || !ROUTINE_PAYER_WAITS.has(value)) return null;
	return value as WebRoutinePayer["waitsFor"];
}

/** The refusals `qorpera_set_routine_login` names, by code and message; anything else is no refusal of the login. */
function loginRefusal(error: unknown): WebRoutineLoginRefusal | undefined {
	if (!(error instanceof CloudRpcError)) return undefined;
	if (error.code === "55000") return error.message === "Company execution is paused" ? "stopped" : undefined;
	if (error.code === "22023")
		switch (error.message) {
			case "This login cannot pay for this routine":
				return "cannot_pay";
			case "This login has reached its usage limit":
				return "usage_limit";
			case "This login needs a new sign-in":
				return "sign_in";
			default:
				return undefined;
		}
	if (error.code !== "42501") return undefined;
	switch (error.message) {
		case "Routine is unavailable":
			return "unavailable";
		case "This login cannot pay for this routine":
			return "not_yours";
		case "A ChatGPT plan cannot pay for a listener":
			return "plan_listener";
		default:
			return undefined;
	}
}

/** A Claude login is for coding only: it never pays for an agent's routines. */
export function claudeLogin(account: WebMemberConnection): boolean {
	return account.provider === "anthropic" || account.nativeHarness === "claude-code";
}

/** Whether an account can pay for a routine's runs: never a Claude login, and a ChatGPT plan only once its member
 * turned on work while away, and never for a routine that starts on a file change or a webhook. */
export function canPayForRoutine(account: WebMemberConnection, listening = false): boolean {
	return !claudeLogin(account) && (!planLogin(account) || (account.allowAutonomous === true && !listening));
}

function record(value: unknown): Record<string, unknown> {
	if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("Invalid cloud response");
	return value as Record<string, unknown>;
}

/** One sponsor per account version: saving again reuses it instead of piling up. */
async function sponsorKey(connectionId: string, version: number): Promise<string> {
	const bytes = new Uint8Array(
		await crypto.subtle.digest(
			"SHA-256",
			new TextEncoder().encode(`qorpera.routine-sponsor/v1:${connectionId}:${version}`),
		),
	).slice(0, 16);
	bytes[6] = (bytes[6]! & 0x0f) | 0x80;
	bytes[8] = (bytes[8]! & 0x3f) | 0x80;
	const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** The gateway refused the change set before the core saw it (a malformed request, the member's access, a company
 * paused or being deleted): nothing was written. A lock conflict (409), the member's change set in flight (429), an
 * outage or no answer at all may have left it written, or may yet. */
function refusedBeforeCommit(error: unknown): boolean {
	const status = error instanceof CloudMemberFolderError ? error.status : null;
	return status !== null && status >= 400 && status < 500 && status !== 408 && status !== 409 && status !== 429;
}

/** What the editor shows of a routine's file. A version_changed is retried only while these are as the form had them. */
function editorFields(file: RoutineFile): string {
	return JSON.stringify([file.title, file.trigger, file.timezone, file.reply, file.instructions]);
}

/** What a draft makes of the editor's fields in a file. */
interface RoutineEdit {
	readonly title: string;
	readonly trigger: RoutineTrigger;
	readonly timezone: string | null;
	readonly reply: RoutineFile["reply"];
	readonly instructions: string;
}

/** A trigger as a file keeps it, without the editor's id or the reply, which an edit may change. */
function keptTrigger(trigger: CloudRoutineTrigger): string {
	const { id: _id, replyPolicy: _reply, ...rest } = trigger;
	return JSON.stringify(rest);
}

function editOf(draft: WebRoutineDraft, file: RoutineFile | null): RoutineEdit {
	const mapped = routineFileTrigger(draft.trigger);
	if (!mapped) {
		// An edit that keeps how its routine runs (a window, a dynamic routine's next run, a webhook): the draft's trigger
		// is the file's as `cloudRoutineTriggerOfFile` reads it, and the file's trigger and zone stay as they are; only
		// the reply is the draft's. A new routine, or a trigger that is not the file's, is never written.
		const own = file && cloudRoutineTriggerOfFile(file);
		if (!file || !own) throw new Error("A member's routine runs every few minutes or on chosen days.");
		if (keptTrigger(own) !== keptTrigger(draft.trigger))
			throw new WebRoutineChangedError("This routine's file has changed.");
		return {
			title: draft.title.trim(),
			trigger: file.trigger,
			timezone: null,
			reply: draft.trigger.replyPolicy ?? "required",
			instructions: draft.prompt.trim(),
		};
	}
	let trigger = mapped.trigger;
	// The same days in another order are no change: the file keeps its own order.
	const before = file?.trigger;
	if (
		trigger.when === "weekly" &&
		before?.when === "weekly" &&
		trigger.days.length === before.days.length &&
		trigger.days.every((day) => before.days.includes(day))
	)
		trigger = { ...trigger, days: before.days };
	return {
		title: draft.title.trim(),
		trigger,
		timezone: mapped.timezone,
		reply: draft.trigger.replyPolicy ?? "required",
		instructions: draft.prompt.trim(),
	};
}

/** An edit of a routine file's text: the settings the edit changes (`setCompanyStateSettings`) and the body when the
 * instructions changed. Effort, the end, the runs, quiet hours, notes and every other byte stay as read. */
function rewriteRoutine(text: string, file: RoutineFile, edit: RoutineEdit): string {
	const settings = routineFileSettings(file, edit);
	const next = Object.keys(settings).length ? setCompanyStateSettings(text, settings) : text;
	return edit.instructions === file.instructions ? next : withStateBody(next, edit.instructions);
}

/** A routine file as this save knows it: the one the editor opened, or one this save wrote (found by its path). */
interface RoutineBase {
	readonly routineId: string | null;
	readonly path: string;
	readonly version: string;
	readonly text: string;
	readonly file: RoutineFile;
}

/** A change set sent whose answer has not come back. */
interface PendingSave {
	readonly key: string;
	readonly units: readonly CloudMemberFolderUnit[];
	/** The draft it was made from. */
	readonly draft: string;
	/** The routine file it writes: its path, its text and that text's parse. */
	readonly path: string;
	readonly text: string;
	readonly file: RoutineFile;
}

interface SaveState {
	base: RoutineBase | null;
	pending: PendingSave | null;
}

/** A draft as a save compares it. The trigger's id is the editor's own: the file never stores it. */
const drafted = (draft: WebRoutineDraft) => {
	const { id: _id, ...trigger } = draft.trigger;
	return JSON.stringify([draft.staffAgentId, draft.title, draft.prompt, trigger, draft.effort]);
};

/** A routine file's text as a save writes it, parsed; the file's own problems when it does not parse. */
function routineOf(path: string, text: string): RoutineFile {
	const parsed = parseStateFile(path, text);
	if (!parsed.ok) throw invalid(NOT_SAVED, parsed.problems);
	if (parsed.file.kind !== "routine") throw invalid(NOT_SAVED, [`${path} is not a routine`]);
	return parsed.file;
}

export function createWebRoutines(
	cloud: Cloud,
	profile: HostedProfile,
	signal: AbortSignal,
	current: () => void,
	/** The member's own client for the company's folder (P27, P34a). */
	memberFolder: () => WebMemberFolder,
): WebRoutines {
	const check = () => {
		signal.throwIfAborted();
		current();
	};
	const rpc = async (name: string, args: Record<string, unknown>) => {
		check();
		const value = await cloud.rpc(name, args);
		check();
		return value;
	};
	const client = new CloudRoutineClient((name, args) => rpc(name, args));
	const folder = () => {
		check();
		return memberFolder();
	};
	/** Today's idempotent consent and binding, so the account has a binding in this company that pays for unattended
	 * work. A ChatGPT plan is never consented here: its member turns on work while away in Settings. */
	const sponsor = async (account: WebMemberConnection) => {
		if (claudeLogin(account)) throw new Error("A Claude login can't pay for routines.");
		if (account.connectionStatus !== "active" || account.authorizationState !== "active")
			throw new Error("This model account needs to be signed in again.");
		let version: number;
		if (planLogin(account)) {
			// A ChatGPT plan works while its member is away only once they turned that on in Settings;
			// saving a routine never turns it on for them.
			if (account.allowAutonomous !== true)
				throw new Error("Turn on “Work while you're away” for this ChatGPT plan in Settings first.");
			version = account.connectionVersion;
		} else {
			const consent = record(
				await rpc("qorpera_consent_provider_connection_autonomous", {
					p_profile_id: profile.profileId,
					p_connection_id: account.connectionId,
					p_allow_autonomous: true,
				}),
			);
			if (
				consent.connection_id !== account.connectionId ||
				consent.allow_autonomous !== true ||
				!Number.isSafeInteger(consent.version)
			)
				throw new Error("Invalid cloud response");
			version = consent.version as number;
		}
		const bound = record(
			await rpc("qorpera_create_member_execution_sponsor", {
				p_profile_id: profile.profileId,
				p_client_binding_id: await sponsorKey(account.connectionId, version),
				p_provider_connection_id: account.connectionId,
				p_provider_connection_version: version,
				p_allow_autonomous: true,
			}),
		);
		if (typeof bound.binding_id !== "string" || !Number.isSafeInteger(bound.version))
			throw new Error("Invalid cloud response");
	};
	/** The login a save needs, once per call, and only once it is known to write. */
	const payer = (account: WebMemberConnection | undefined) => {
		let paid: Promise<void> | undefined;
		return async () => {
			if (!account) throw new WebRoutineNoPayerError("Choose a model account that can pay for this routine.");
			paid ??= sponsor(account);
			await paid;
		};
	};
	const list = async () => {
		// The SDK reads the fields it knows; these come from the same answer.
		let answer: unknown;
		const listed = await new CloudRoutineClient(async (name, args) => {
			const value = await rpc(name, args);
			if (name === "qorpera_list_cloud_routines") answer = value;
			return value;
		}).list(profile.environmentId, signal);
		const rows = Array.isArray(answer) ? (answer as unknown[]) : [];
		return listed.map((routine): WebRoutine => {
			const row = rows.find(
				(item): item is Record<string, unknown> =>
					Boolean(item) &&
					typeof item === "object" &&
					(item as Record<string, unknown>).routine_id === routine.routineId,
			);
			const reason = row?.pause_reason;
			const member = row?.runs_for_user_id;
			const resumed = row?.resume_reason;
			const resumedAt = row?.resumed_at;
			const resumedBy = row?.resumed_by_user_id;
			return {
				...routine,
				...(typeof reason === "string" && /^[a-z_]{1,64}$/.test(reason) ? { pauseReason: reason } : {}),
				...(typeof member === "string" && UUID_PATTERN.test(member) ? { runsForUserId: member } : {}),
				...(typeof resumed === "string" &&
				/^[a-z_]{1,64}$/.test(resumed) &&
				typeof resumedAt === "string" &&
				Number.isFinite(Date.parse(resumedAt)) &&
				typeof resumedBy === "string" &&
				UUID_PATTERN.test(resumedBy)
					? { resumeReason: resumed, resumedAt, resumedByUserId: resumedBy }
					: {}),
			};
		});
	};
	/** A routine's file from the member's state folders (by the index's routine id). */
	const routinePath = async (from: WebMemberFolder, routineId: string): Promise<string> => {
		const listed = (await from.stateFolders(signal)).routines;
		check();
		const path = listed?.find((item) => item.routineId === routineId)?.file;
		if (!path || companyStatePath(path)?.kind !== "routine")
			throw new WebRoutineGoneError("This routine has no file you can change.");
		return path;
	};
	/** A routine file's text, version and parse; null when nothing is at the path. */
	const readRoutine = async (
		from: WebMemberFolder,
		path: string,
		routineId: string | null,
		id: string,
	): Promise<RoutineBase | null> => {
		const read = await readStateFile(from, path, signal);
		check();
		if (!read) return null;
		const parsed = parseStateFile(path, read.text);
		if (!parsed.ok || parsed.file.kind !== "routine" || parsed.file.id !== id)
			throw new WebRoutineChangedError("This routine's file has changed.");
		return { routineId, path, version: read.entry.version, text: read.text, file: parsed.file };
	};
	/** The file again after a version_changed: its new version, while what the form shows is as it opened. */
	const reread = async (from: WebMemberFolder, known: RoutineBase, opened: string): Promise<RoutineBase> => {
		const path = known.routineId === null ? known.path : await routinePath(from, known.routineId);
		const read = await readRoutine(from, path, known.routineId, known.file.id);
		if (!read) throw new WebRoutineGoneError("This routine is no longer available.");
		// A change to what the form shows is the member's to see before anything is written over it.
		if (editorFields(read.file) !== opened)
			throw new WebRoutineChangedError("This routine changed while you were editing it.");
		return read;
	};
	/** A payer call's answer, or null on a cloud without the payer calls: the first such answer stops them for the
	 * page's life. A pick's refusal is a `WebRoutineLoginRefusedError`. */
	const payerCall = async (name: string, args: Record<string, unknown>, routineId: string) => {
		if (payerless.has(cloud)) return null;
		let value: unknown;
		try {
			value = await rpc(name, args);
		} catch (error) {
			if (error instanceof CloudRpcError && error.missing) {
				payerless.add(cloud);
				return null;
			}
			const refusal = name === "qorpera_set_routine_login" ? loginRefusal(error) : undefined;
			throw refusal ? new WebRoutineLoginRefusedError(refusal) : error;
		}
		return parseRoutinePayer(value, routineId);
	};
	const saves = new Map<string, SaveState>();
	/** Sends a change set, kept as the save's pending one until an answer comes. */
	const send = async (
		from: WebMemberFolder,
		state: SaveState,
		pending: PendingSave,
	): Promise<readonly CloudMemberFolderUnitResult[]> => {
		state.pending = pending;
		let answer: CloudMemberFolderCommit;
		try {
			answer = await from.commit(pending.key, pending.units, signal);
		} catch (error) {
			if (refusedBeforeCommit(error)) state.pending = null;
			throw error;
		}
		state.pending = null;
		check();
		return answer.units;
	};
	/** A new routine's file: `QorperaOS/agents/<handle>/routines/<key>.md`, its key from the title, free in the member's
	 * listing. The routines folder comes first in the same change set when it is missing. */
	const createFile = async (
		from: WebMemberFolder,
		state: SaveState,
		draft: WebRoutineDraft,
		login: () => Promise<void>,
	): Promise<"saved"> => {
		const fields = editOf(draft, null);
		const text = writeRoutineFile({
			id: draft.clientRoutineId,
			title: fields.title,
			status: "active",
			trigger: fields.trigger,
			timezone: fields.timezone,
			ends: null,
			runs: null,
			quiet: null,
			effort: draft.effort,
			reply: fields.reply,
			conclusion: null,
			instructions: fields.instructions,
		});
		const tried = new Set<string>();
		for (let attempt = 1; ; attempt++) {
			const agent = (await from.stateFolders(signal)).agents.find(
				(item) => item.staffAgentId === draft.staffAgentId,
			)?.file;
			check();
			if (!agent || companyStatePath(agent)?.kind !== "agent")
				throw new WebRoutineGoneError("This agent's folder is not available.");
			const routines = `${parentPath(agent)}/routines`;
			const listed = await from.read(routines, signal);
			check();
			if (listed.entry && listed.entry.kind !== "folder") throw new Error(`${routines} is not a folder`);
			const taken = new Set(tried);
			if (listed.entry)
				for (const name of await folderNames(from, routines, signal))
					if (name.endsWith(".md")) taken.add(name.slice(0, -3));
			check();
			const key = freeCompanyStateName(fields.title, "routine", taken);
			const path = `${routines}/${key}.md`;
			const file = routineOf(path, text);
			await login();
			const units: CloudMemberFolderUnit[] = [
				...(listed.entry ? [] : [{ op: "mkdir" as const, path: routines }]),
				{ op: "write", path, text },
			];
			const results = await send(from, state, {
				key: `web:routine-create:${crypto.randomUUID()}`,
				units,
				draft: drafted(draft),
				path,
				text,
				file,
			});
			const write = results.at(-1)!;
			const made = listed.entry ? null : results[0]!;
			if (write.outcome === "saved") return "saved";
			if (write.outcome === "conflict" && write.reason === "exists") {
				// The file there with this draft's id is this routine, saved by a try whose answer was lost.
				const there = await readStateFile(from, path, signal);
				check();
				const found = there ? parseStateFile(path, there.text) : null;
				if (found?.ok && found.file.kind === "routine" && found.file.id === draft.clientRoutineId) return "saved";
				if (attempt < CREATE_TRIES) {
					tried.add(key);
					continue;
				}
			} else if (made?.outcome === "conflict" && made.reason === "exists" && attempt < CREATE_TRIES)
				// Another writer made the routines folder since the read: read again, and write without it.
				continue;
			throw unsaved(made && made.outcome !== "saved" && write.reason === "parent_missing" ? made : write, NOT_SAVED);
		}
	};
	/** An edit of a routine's file from what this save knows of it. */
	const editFile = async (
		from: WebMemberFolder,
		state: SaveState,
		base: RoutineBase,
		draft: WebRoutineDraft,
		login: () => Promise<void>,
	): Promise<"saved" | "unchanged"> => {
		const opened = editorFields(base.file);
		let known = base;
		for (let attempt = 0; ; attempt++) {
			const text = rewriteRoutine(known.text, known.file, editOf(draft, known.file));
			const file = routineOf(known.path, text);
			if (JSON.stringify(file) === JSON.stringify(known.file)) return attempt ? "saved" : "unchanged";
			await login();
			const [result] = await send(from, state, {
				key: `web:routine-update:${crypto.randomUUID()}`,
				units: [{ op: "write", path: known.path, text, expectedVersion: known.version }],
				draft: drafted(draft),
				path: known.path,
				text,
				file,
			});
			if (result!.outcome === "saved") return "saved";
			if (result!.outcome === "conflict" && result!.reason === "version_changed" && attempt < RETRIES) {
				state.base = known = await reread(from, known, opened);
				continue;
			}
			throw unsaved(result!, NOT_SAVED);
		}
	};
	return {
		list,
		runs: () => client.occurrences(profile.environmentId, signal, 200),
		async files() {
			const listed = (await folder().stateFolders(signal)).routines;
			check();
			return new Set((listed ?? []).map((item) => item.routineId));
		},
		async open(routine) {
			const from = folder();
			const path = await routinePath(from, routine.routineId);
			const read = await readRoutine(from, path, routine.routineId, routine.clientRoutineId);
			if (!read) throw new WebRoutineGoneError("This routine is no longer available.");
			const when = read.file.trigger.when;
			const trigger = when === "every" || when === "weekly" ? cloudRoutineTriggerOfFile(read.file) : null;
			return { ...read, routineId: routine.routineId, ...(trigger ? { trigger } : {}) };
		},
		async save(draft, account, saveId) {
			const from = folder();
			const state = saves.get(saveId) ?? { base: draft.opened ?? null, pending: null };
			saves.set(saveId, state);
			try {
				const login = payer(account);
				// A change set whose answer never came goes again first, as it was: a replay when it was saved.
				const lost = state.pending;
				let replayed = false;
				if (lost) {
					await login();
					const results = await send(from, state, lost);
					const write = results.at(-1)!;
					if (write.outcome === "saved") {
						if (lost.draft === drafted(draft)) {
							saves.delete(saveId);
							return "saved";
						}
						// The draft changed since: this edit starts from the file as that change set wrote it, at the version
						// its answer gives, so a change made since meets version_changed and is checked against what it wrote.
						if (!write.version) throw new Error("Invalid cloud response");
						state.base = {
							routineId: state.base?.routineId ?? null,
							path: lost.path,
							version: write.version,
							text: lost.text,
							file: lost.file,
						};
						replayed = true;
					} else if (write.outcome === "conflict" && write.reason === "version_changed" && state.base)
						state.base = await reread(from, state.base, editorFields(state.base.file));
					else if (write.outcome !== "conflict") {
						// Refused or denied: nothing was written, and the same draft would be refused again.
						const made = results.length > 1 ? results[0]! : null;
						throw unsaved(
							made && made.outcome !== "saved" && write.reason === "parent_missing" ? made : write,
							NOT_SAVED,
						);
					}
					// Any other conflict (a name taken, a folder made meanwhile) is the steps' own, from a new read.
				}
				const result = state.base
					? await editFile(from, state, state.base, draft, login)
					: await createFile(from, state, draft, login);
				saves.delete(saveId);
				// The replay wrote this save's routine, even when the draft's change since adds nothing to it.
				return replayed ? "saved" : result;
			} catch (error) {
				// Failed with nothing sent unanswered and nothing learned since the form opened: the next save with this
				// id starts as a first one would, so the entry goes. So it does after a change to what the form shows: the
				// editor opens the routine again from its file (a new routine too, once a lost answer's replay made it), and
				// the base kept here could only meet that change again.
				if (
					state.pending === null &&
					(error instanceof WebRoutineChangedError || state.base === (draft.opened ?? null))
				)
					saves.delete(saveId);
				throw error;
			}
		},
		async setEnabled(routine, account, enabled, saveId) {
			const from = folder();
			// A routine without a file this member can change stops here, before any login.
			const path = await routinePath(from, routine.routineId);
			if (enabled) {
				if (!account) throw new WebRoutineNoPayerError("Choose a model account that can pay for this routine.");
				if (planLogin(account) && routine.triggers.some((item) => item.kind === "listener"))
					throw new Error(LISTENER_ON_PLAN);
				await sponsor(account);
			}
			let tries = 0;
			await rewriteStateFile(
				from,
				{
					locate: async () => {
						if (tries++ === 0) return path;
						// The file changed under the switch: a routine that ended meanwhile stays as it is.
						const row = (await list()).find((item) => item.routineId === routine.routineId);
						if (!row || row.status === "expired" || row.status === "concluded" || row.status === "retired")
							throw new WebRoutineEndedError("This routine has ended.");
						return routinePath(from, routine.routineId);
					},
					rewrite: (text, at) => {
						const parsed = parseStateFile(at, text);
						if (!parsed.ok || parsed.file.kind !== "routine" || parsed.file.id !== routine.clientRoutineId)
							throw new WebRoutineChangedError("This routine's file has changed.");
						if (parsed.file.status === "concluded") throw new WebRoutineEndedError("This routine has ended.");
						if (!enabled)
							return parsed.file.status === "paused"
								? null
								: setCompanyStateSettings(text, { status: "paused" });
						// An active file saved as it is ends a pause the platform recorded in the index only, and pays,
						// as the runtime's manage_routine does.
						return parsed.file.status === "active" ? text : setCompanyStateSettings(text, { status: "active" });
					},
					key: () => `web:routine-${enabled ? "resume" : "pause"}:${saveId}:${tries}`,
					gone: "This routine is no longer available.",
					failure: enabled ? "The routine could not be resumed." : "The routine could not be paused.",
				},
				signal,
				check,
			);
		},
		payer: (routineId) => payerCall("qorpera_get_routine_payer", { p_routine_id: routineId }, routineId),
		async setLogin(routineId, account) {
			if (payerless.has(cloud)) return null;
			// The pick consents to nothing: it takes a login only with a binding that pays for work while its member is
			// away. So the login gets a save's consent and binding first (a ChatGPT plan only once its member turned
			// that on, and then without asking again).
			await sponsor(account);
			return payerCall(
				"qorpera_set_routine_login",
				{ p_routine_id: routineId, p_connection_id: account.connectionId },
				routineId,
			);
		},
	};
}
